<template>
  <div>
    <b-card no-body class="mb-0">
      <div class="m-2">
        <!-- Header -->
        <b-row class="mb-2">
          <b-col cols="12">
            <h3 class="mb-0">{{ $t('Cobertura DTC por Modelo') }}</h3>
            <p class="text-muted">{{ $t('Veja quais modelos possuem tabelas DTC associadas e gerencie a cobertura.') }}</p>
          </b-col>
        </b-row>

        <!-- Filtros e KPIs -->
        <b-row class="mb-2 align-items-center">
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <b-form-input
              v-model="searchQuery"
              :placeholder="$t('Buscar modelo, fabricante ou tabela...')"
              @input="debounceSearch"
            />
          </b-col>
          
          <b-col cols="12" md="3" class="mb-1 mb-md-0">
            <v-select
              v-model="manufacturerId"
              :options="manufacturers"
              :reduce="m => m.id"
              label="name"
              :placeholder="$t('Todos os Fabricantes')"
              @input="onFilterChange"
            />
          </b-col>

          <b-col cols="12" md="6" class="d-flex justify-content-md-end">
            <b-form-radio-group
              v-model="filterHasTable"
              :options="hasTableOptions"
              buttons
              button-variant="outline-primary"
              name="radio-btn-outline"
              @change="onFilterChange"
            />
          </b-col>
        </b-row>
      </div>

      <!-- Tabela -->
      <b-table
        class="position-relative"
        :items="items"
        :fields="columns"
        responsive
        show-empty
        :empty-text="$t('Nenhuma cobertura encontrada com os filtros atuais.')"
        hover
        :busy="isLoading"
      >
        <template #table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner class="align-middle"></b-spinner>
            <strong class="ml-1">{{ $t('Carregando cobertura...') }}</strong>
          </div>
        </template>

        <!-- Coluna: Modelo -->
        <template #cell(assetModelName)="data">
          <div class="d-flex align-items-center">
            <b-avatar rounded size="32" variant="light-primary" class="mr-1">
              <feather-icon icon="TruckIcon" size="16" />
            </b-avatar>
            <span class="font-weight-bold">{{ data.value }}</span>
          </div>
        </template>

        <!-- Coluna: Status -->
        <template #cell(status)="data">
          <b-badge :variant="data.item.hasTable ? 'light-success' : 'light-warning'" class="mr-50">
            {{ data.item.hasTable ? $t('Com tabela') : $t('Sem tabela') }}
          </b-badge>
          <b-badge v-if="data.item.tableCount > 1" variant="light-info">
            {{ data.item.tableCount }} {{ $t('tabelas') }}
          </b-badge>
        </template>

        <!-- Coluna: Tabela DTC -->
        <template #cell(dtcTableName)="data">
          <div v-if="data.item.hasTable && data.item.tables && data.item.tables.length > 0">
            <b-badge
              v-for="(table, index) in data.item.tables"
              :key="table.id"
              :variant="index % 2 === 0 ? 'light-primary' : 'light-secondary'"
              class="mr-50 mb-50 cursor-pointer"
              @click="$router.push({ name: 'edit-dtc-table', params: { id: table.id }, query: { tab: 'models' } })"
              v-b-tooltip.hover.top="$t('Abrir tabela')"
            >
              {{ table.name }}
            </b-badge>
          </div>
          <span v-else class="text-muted">-</span>
        </template>

        <!-- Coluna: Ações -->
        <template #cell(actions)="data">
          <div class="text-nowrap" v-if="data.item.hasTable">
            <!-- Vincular outra (novo) -->
            <b-button
              variant="flat-success"
              class="btn-icon mr-50"
              @click="openLinkModal(data.item)"
              v-b-tooltip.hover.top="$t('Vincular outra')"
            >
              <feather-icon icon="PlusIcon" />
            </b-button>

            <!-- Abrir tabela (se tiver só 1, botão direto, senao dropdown) -->
            <b-button
              v-if="data.item.tableCount === 1"
              variant="flat-primary"
              class="btn-icon mr-50"
              @click="$router.push({ name: 'edit-dtc-table', params: { id: data.item.tables[0].id }, query: { tab: 'models' } })"
              v-b-tooltip.hover.top="$t('Abrir tabela')"
            >
              <feather-icon icon="ExternalLinkIcon" />
            </b-button>
            <b-dropdown
              v-else
              variant="flat-primary"
              class="mr-50 btn-icon dropdown-icon-wrapper"
              no-caret
              right
              v-b-tooltip.hover.top="$t('Abrir tabela...')"
            >
              <template #button-content>
                <feather-icon icon="ExternalLinkIcon" />
              </template>
              <b-dropdown-item
                v-for="table in data.item.tables"
                :key="table.id"
                @click="$router.push({ name: 'edit-dtc-table', params: { id: table.id }, query: { tab: 'models' } })"
              >
                {{ table.name }}
              </b-dropdown-item>
            </b-dropdown>

            <!-- Desvincular -->
            <b-button
              variant="flat-danger"
              class="btn-icon"
              @click="confirmUnlink(data.item)"
              v-b-tooltip.hover.top="$t('Desvincular')"
            >
              <feather-icon icon="Link2OffIcon" />
            </b-button>
          </div>
          <div class="text-nowrap" v-else>
            <b-button
              variant="primary"
              size="sm"
              class="mr-50"
              @click="openLinkModal(data.item)"
            >
              {{ $t('Vincular') }}
            </b-button>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="$router.push({ name: 'create-dtc-table', query: { assetModelId: data.item.assetModelId } })"
            >
              {{ $t('Criar') }}
            </b-button>
          </div>
        </template>
      </b-table>

      <!-- Paginação -->
      <div class="mx-2 mb-2">
        <b-row>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-start">
            <span class="text-muted">{{ $t('Total de modelos encontrados:') }} {{ totalItems }}</span>
          </b-col>
          <b-col cols="12" sm="6" class="d-flex align-items-center justify-content-center justify-content-sm-end">
            <b-pagination
              v-model="currentPage"
              :total-rows="totalItems"
              :per-page="perPage"
              first-number
              last-number
              class="mb-0 mt-1 mt-sm-0"
            />
          </b-col>
        </b-row>
      </div>
      
      <!-- Modal Desvincular -->
      <b-modal
        id="unlink-table-modal"
        ref="unlinkModal"
        :title="$t('Desvincular Tabela')"
        size="md"
        cancel-variant="outline-secondary"
        :ok-title="$t('Desvincular')"
        :cancel-title="$t('Cancelar')"
        @ok="handleUnlinkOk"
        @hidden="resetUnlinkModal"
        :ok-disabled="!selectedTableToUnlink || isUnlinking"
        ok-variant="danger"
        no-close-on-backdrop
        ignore-enforce-focus-selector=".vs__dropdown-menu, .vs__dropdown-menu *"
      >
        <p>{{ $t('Selecione qual tabela deseja desvincular do modelo') }} <strong>{{ selectedModelForUnlink.name }}</strong>:</p>
        <b-form-group>
          <v-select
            v-model="selectedTableToUnlink"
            :options="selectedModelForUnlink.tables || []"
            :reduce="table => table.id"
            label="name"
            :placeholder="$t('Selecione a tabela...')"
          />
        </b-form-group>
      </b-modal>

    </b-card>

    <link-existing-table-modal
      ref="linkModal"
      :asset-model-id="selectedModelForLink.id"
      :model-name="selectedModelForLink.name"
      @linked="fetchCoverage"
    />
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormInput, BFormRadioGroup, BTable, BPagination, BButton, BBadge, BAvatar, BLink, BSpinner, VBTooltip, BDropdown, BDropdownItem, BModal, BFormGroup
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import store from '@/store'
import dtcStoreModule from './dtcStoreModule'
import manufacturerStoreModule from '@/views/main/asset_manufacturer/manufacturerStoreModule'
import LinkExistingTableModal from './components/LinkExistingTableModal.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard, BRow, BCol, BFormInput, BFormRadioGroup, BTable, BPagination, BButton, BBadge, BAvatar, BLink, BSpinner, BDropdown, BDropdownItem, BModal, BFormGroup,
    vSelect, LinkExistingTableModal
  },
  directives: {
    'b-tooltip': VBTooltip
  },
  data() {
    return {
      searchQuery: '',
      manufacturerId: null,
      filterHasTable: 'false', // Default "Sem tabela" para foco em lacunas
      manufacturers: [],
      items: [],
      isLoading: false,
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      searchTimeout: null,
      selectedModelForLink: { id: null, name: '' },
      selectedModelForUnlink: { id: null, name: '', tables: [] },
      selectedTableToUnlink: null,
      isUnlinking: false,
      hasTableOptions: [
        { text: this.$t('Todos'), value: 'all' },
        { text: this.$t('Com Tabela'), value: 'true' },
        { text: this.$t('Sem Tabela'), value: 'false' }
      ],
      columns: [
        { key: 'assetModelName', label: this.$t('Modelo') },
        { key: 'assetManufacturerName', label: this.$t('Fabricante') },
        { key: 'status', label: this.$t('Status') },
        { key: 'dtcTableName', label: this.$t('Tabela DTC') },
        { key: 'actions', label: this.$t('Ações') }
      ]
    }
  },
  watch: {
    currentPage() {
      this.fetchCoverage()
    }
  },
  created() {
    const DTC_APP_STORE_MODULE_NAME = 'app-dtc'
    if (!store.hasModule(DTC_APP_STORE_MODULE_NAME)) {
      store.registerModule(DTC_APP_STORE_MODULE_NAME, dtcStoreModule)
    }

    const MANUFACTURER_APP_STORE_MODULE_NAME = 'app-manufacturer'
    if (!store.hasModule(MANUFACTURER_APP_STORE_MODULE_NAME)) {
      store.registerModule(MANUFACTURER_APP_STORE_MODULE_NAME, manufacturerStoreModule)
    }

    this.fetchManufacturers()
    this.fetchCoverage()
  },
  methods: {
    debounceSearch() {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.fetchCoverage()
      }, 500)
    },
    onFilterChange() {
      this.currentPage = 1
      this.fetchCoverage()
    },
    fetchManufacturers() {
      store.dispatch('app-manufacturer/fetchManufacturers', {
        query: '', page: 1, perPage: 100 // Pega os primeiros 100 ou poderia usar um v-select assíncrono
      }).then(response => {
        this.manufacturers = response.data?.data || []
      })
    },
    fetchCoverage() {
      this.isLoading = true
      let hasTableParam = this.filterHasTable
      
      store.dispatch('app-dtc/fetchCoverage', {
        q: this.searchQuery,
        manufacturerId: this.manufacturerId,
        hasTable: hasTableParam,
        page: this.currentPage,
        perPage: this.perPage
      }).then(response => {
        this.items = response.data?.data || []
        this.totalItems = response.data?.totalItems || 0
      }).catch(() => {
        this.$toast({
          component: ToastificationContent,
          props: { title: this.$t('Erro ao carregar cobertura'), icon: 'AlertTriangleIcon', variant: 'danger' }
        })
      }).finally(() => {
        this.isLoading = false
      })
    },
    openLinkModal(item) {
      this.selectedModelForLink = {
        id: item.assetModelId,
        name: item.assetModelName
      }
      this.$refs.linkModal.show()
    },
    confirmUnlink(item) {
      if (item.tableCount > 1) {
        this.selectedModelForUnlink = {
          id: item.assetModelId,
          name: item.assetModelName,
          tables: item.tables || []
        }
        this.$refs.unlinkModal.show()
      } else {
        const table = (item.tables && item.tables[0]) || {}
        this.$swal({
          title: this.$t('Remover vínculo?'),
          text: this.$t(`Tem certeza que deseja desvincular a tabela ${table.name || ''} do modelo ${item.assetModelName}?`),
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: this.$t('Sim, remover'),
          cancelButtonText: this.$t('Cancelar'),
          customClass: { confirmButton: 'btn btn-danger', cancelButton: 'btn btn-outline-secondary ml-1' },
          buttonsStyling: false,
        }).then(result => {
          if (result.value) {
            this.executeUnlink(table.id, item.assetModelId)
          }
        })
      }
    },
    handleUnlinkOk(bvModalEvent) {
      bvModalEvent.preventDefault()
      if (!this.selectedTableToUnlink || !this.selectedModelForUnlink.id) return

      this.isUnlinking = true
      this.executeUnlink(this.selectedTableToUnlink, this.selectedModelForUnlink.id)
    },
    executeUnlink(tableId, modelId) {
      store.dispatch('app-dtc/unlinkModel', { dtcTableId: tableId, assetModelId: modelId })
        .then(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Vínculo removido'), icon: 'CheckIcon', variant: 'success' }
          })
          this.fetchCoverage()
          if (this.$refs.unlinkModal) {
            this.$refs.unlinkModal.hide()
          }
        })
        .catch(() => {
          this.$toast({
            component: ToastificationContent,
            props: { title: this.$t('Erro ao remover vínculo'), icon: 'AlertTriangleIcon', variant: 'danger' }
          })
        })
        .finally(() => {
          this.isUnlinking = false
        })
    },
    resetUnlinkModal() {
      this.selectedTableToUnlink = null
      this.selectedModelForUnlink = { id: null, name: '', tables: [] }
    }
  }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';

.btn-icon {
  padding: 0.5rem;
}
.cursor-pointer {
  cursor: pointer;
}
.dropdown-icon-wrapper > button {
  padding: 0.5rem !important;
}
</style>
